import React, { useMemo, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import launchSourceOne from "../../../../utilities/requests/launchSourceOne";
import ECLImageViz from "../Visualizations/ECLImageViz";
import { ECLGaugeRichText, ECLLoader, ECLUserInfo } from "../../../index";
import ECLRichTextTable from "../Visualizations/ECLRichTextTable";
import ECLRichTextDataViz from "../Visualizations/ECLRichTextDataViz";
import ColorPlugin from "../Plugins/ColorPlugin";
import "./ECLRichTextViewer.css";
import useSelectorId from "../../../../utilities/hooks/useSelectorId";
import { AppContext } from "../../../../utilities/contexts";
import { Link } from "react-router-dom";
import ECLRichTextCodeBlock from "../Visualizations/ECLRichTextCodeBlock";

export const ECLRichTextViz = (props) => {
   const viewer = useMemo(
      () => (
         <ECLRichTextViewer
            key={props.data.info.text.slice(0, 30)}
            textAsMarkdown={props.data.info.text}
         />
      ),
      [props.data.info.text],
   );
   return <div>{viewer}</div>;
};

const ECLRichTextViewer = (props) => {
   const users = useContext(AppContext);
   if (!users) {
      return <ECLLoader />;
   }
   const instanceId = useSelectorId();
   //EM, Strong and Del tags are placeholders to simulate inline styling, needs research/big rich text styling ticket.
   return (
      <div id={props.id ? props.id : `rich-text-viewer${instanceId}`}>
         <ReactMarkdown
            className="rich-text-container"
            transformLinkUri={null}
            components={{
               img: ({ ...gfmProps }) => (
                  <ImgRenderer key={props.src} {...gfmProps} />
               ),
               a: ({ ...gfmProps }) => (
                  <LauncherRenderer
                     {...gfmProps}
                     reactProps={props}
                     project_id={props.project_id}
                  />
               ),
               p: ({ ...gfmProps }) => (
                  <span className="rich-text-span" {...gfmProps} />
               ), //Necessary for nesting complex components into markdown
               em: ({ ...gfmProps }) => (
                  <span style={{ fontStyle: "italic" }} {...gfmProps} />
               ),
               strong: ({ ...gfmProps }) => (
                  <span style={{ fontWeight: "bold" }} {...gfmProps} />
               ),
               del: ({ ...gfmProps }) => (
                  <span
                     style={{ textDecoration: "line-through" }}
                     {...gfmProps}
                  />
               ),
               hr: ({ ...gfmProps }) => <hr {...gfmProps} />,
               table: ({ ...gfmProps }) => <ECLRichTextTable {...gfmProps} />,
               code: ({ ...gfmProps }) => (
                  <ECLRichTextCodeBlock {...gfmProps} />
               ),
            }}
            remarkPlugins={[ColorPlugin, remarkGfm]}
         >
            {props.textAsMarkdown}
         </ReactMarkdown>
      </div>
   );
};

function ImgRenderer(props) {
   // Props.src contains the relevant information.  Split on the colon to separate tag from
   // UUID information
   const splitter = props.src.split(":");
   //If there are no colons to split, it is not a valid viz
   if (splitter.length < 2) {
      return null;
   }

   //tag will be the first element of splitter
   const tag = splitter[0];
   if (splitter.length > 2) {
      // Handle Gauges and other viz with several colons using the label tag.  Shift the label tag
      // and then compare with possible several colon components (ATM, just gauges)
      const labelTag = splitter.shift();
      if (labelTag === "gauge") {
         return (
            <ECLGaugeRichText
               leftLabel={splitter[0]}
               currentVal={splitter[1]}
               rightLabel={splitter[2]}
               maxVal={splitter[3]}
            />
         );
      }
      return null;
   }
   //Clear out the '{' '}', which have been converted into '%7B' and '%7D', and split by pipes '%7C'.
   //if no pipes, access UUID at UUIDList[0]
   const UUIDList = splitter[1]
      .replaceAll("%7B", "")
      .replaceAll("%7D", "")
      .split("%7C");
   // Generate component based on tag
   switch (tag) {
      case "usr":
         return <ECLUserInfo user={UUIDList[0]} richText={true}></ECLUserInfo>;
      case "img":
         return (
            <ECLImageViz
               key={UUIDList[0]}
               object_id={UUIDList[0]}
            ></ECLImageViz>
         );
      case "vis":
         return (
            <ECLRichTextDataViz
               project_id={props.project_id}
               object_id={UUIDList[0]}
               revision_id={UUIDList[1]}
               dataViz_id={UUIDList[2]}
            ></ECLRichTextDataViz>
         );
      default:
         //handle Viz from list
         return null;
   }
}

const LauncherRenderer = (props) => {
   const apps = useContext(AppContext);
   const splitter = props.href.split(":");
   if (splitter[0].includes("http") || splitter.length < 2) {
      //look for http prefix or no split (I.E. 'www.')
      return (
         <a title={props.title} href={"//" + props.href}>
            {props.children}
         </a>
      );
   }
   if (splitter[0] === "ba") {
      //ugghhhh
      function updateAndNavigateInApp(splitter, props) {
         props.reactProps.setNavigateTo(
            splitter[1]
               .replaceAll("%7B", "")
               .replaceAll("%7D", "")
               .toLowerCase(),
         );
         //Now you need to set all the appropriate state to toggle sections, tabs above the desired block, to allow scroll navigation.
         //To do that, find all the sections and tabs it could be hiding under.
         let collapsibleAncestors = [];
         let currentID = Object.entries(props.reactProps.flatDoc).find(
            ([, b]) =>
               b.id ===
               splitter[1]
                  .replaceAll("%7B", "")
                  .replaceAll("%7D", "")
                  .toLowerCase(),
         )[0];
         while (currentID) {
            const [parentKey, parentBlock] = Object.entries(
               props.reactProps.flatDoc,
            ).find(([, b]) => b.children.includes(currentID));
            //Need to know which tab, so push a tuple of tab key and tab index
            if (parentBlock.type === "Tabs") {
               collapsibleAncestors.push([
                  parentKey,
                  Number(currentID.split("-").at(-1)),
               ]);
            }
            //If section, just go with key and "section", so that we're using tuples through the collapsibleAncestor array
            if (parentBlock.type === "Section") {
               collapsibleAncestors.push([parentKey, "section"]);
            }

            if (parentKey.endsWith("_root")) {
               currentID = null;
            } else {
               currentID = parentKey;
            }
         }

         collapsibleAncestors.forEach((c) => {
            if (c[1] === "section") {
               props.reactProps.setFlatDoc((f) => {
                  return {
                     ...f,
                     [c[0]]: {
                        ...f[c[0]],
                        react: {
                           ...f[c[0]].react,
                           collapsed: false,
                        },
                     },
                  };
               });
            } else {
               props.reactProps.setFlatDoc((f) => {
                  return {
                     ...f,
                     [c[0]]: {
                        ...f[c[0]],
                        react: {
                           ...f[c[0]].react,
                           activeTab: c[1],
                        },
                     },
                  };
               });
            }
         });
      }
      //Use props.setFlatDoc to activate all tabs/sections on way to link
      return (
         <a
            title={props.title}
            onClick={(e) => {
               e.preventDefault();
               updateAndNavigateInApp(splitter, props);
            }}
         >
            {props.children}
         </a>
      );
      //
   }

   //Parse, then add in the projectID
   let UUIDList = splitter[1]
      .replaceAll("%7B", "")
      .replaceAll("%7D", "")
      .split("%7C");

   //If the first UUID is an app, navigate to the app
   if (apps.all.find((a) => a.id === UUIDList[0].toLowerCase())) {
      return (
         <Link
            title={props.title}
            to={`/sourceone/applications/app/${UUIDList[0].toLowerCase()}`}
         >
            {props.children}
         </Link>
      );
   }

   //Otherwise add project_ID to the front of the UUID list for launchSourceOne function
   UUIDList.unshift(props.project_id);

   return (
      <a
         title={props.title}
         onClick={(e) => {
            e.preventDefault();
            launchSourceOne(splitter[0], UUIDList);
         }}
      >
         {props.children}
      </a>
   );
};

export default ECLRichTextViewer;
